// ReliabilityResilienceDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
// (Bootstrap + React Router v4/v5)
// Updated to match your “Template / Example badge on the right” style (like InterviewGamePlanDetail.jsx, etc.)
// Also: more readable + more detailed, and uses one consistent running example: “Facebook-like Home Feed”.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      // On route navigation, try again (safe to ignore failures)
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small UI helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

function BadgeRight({ children, tone = "secondary" }) {
  return (
    <span className={`badge bg-${tone} text-white`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function LabeledCard({ label, tone, title, children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone={tone}>{label}</BadgeRight>
        </div>
        <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const TemplateCard = ({ title = "Template", children }) => (
  <LabeledCard label="Template" tone="secondary" title={title}>
    {children}
  </LabeledCard>
);

const ExampleCard = ({ title = "Facebook-like Home Feed — example", children }) => (
  <LabeledCard label="Example" tone="success" title={title}>
    {children}
  </LabeledCard>
);

function CodeCard({ title = "Template — pseudo-code", children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone="secondary">Template</BadgeRight>
        </div>
        <pre
          className="bg-light rounded p-3 border small mb-0"
          style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
        >
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );
}

function SayCallout({ children }) {
  return (
    <div className="alert alert-primary d-flex align-items-center mb-3" role="alert">
      <div className="me-2 fw-semibold">Say</div>
      <div className="mb-0">{children}</div>
    </div>
  );
}

const TOC = [
  { id: "health-grace", label: "A) Health checks & degradation" },
  { id: "retries-breakers", label: "B) Retries, jitter, breakers, bulkheads" },
  { id: "rate-limit", label: "C) Rate limiting & quotas" },
  { id: "backpressure", label: "D) Backpressure (sync & streams)" },
  { id: "dr", label: "E) Disaster recovery (RTO/RPO)" },
  { id: "multiaz-region", label: "F) Multi-AZ vs multi-region" },
  { id: "metrics-slos", label: "G) Metrics, SLOs, guardrails" },
  { id: "soundbites", label: "H) Sound bites" },
];

/* ================== page ================== */
export default function ReliabilityResilienceDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  const feed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      slos: { readP95: "≤ 200ms", writeP95: "≤ 400ms" },
      deps: ["ranking-service", "graph-service", "cache (Redis)", "DB", "Kafka"],
    }),
    []
  );

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Reliability &amp; resilience</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(13,110,253,.07), rgba(111,66,193,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">8) Reliability &amp; resilience — detailed guide</h1>
          <p className="text-muted mb-0">
            Health checks, graceful degradation, retries &amp; jitter, circuit breakers, bulkheads, rate limiting,
            backpressure, DR (RTO/RPO), and multi-AZ/region patterns — with a {feed.name} example in every section.
          </p>
        </div>
      </header>

      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none order-xl-1">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id, label }) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className={`nav-link px-0 ${activeId === id ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === id ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 order-xl-2">
          {/* A) Health checks & degradation */}
          <Section id="health-grace" title="A) Health checks & graceful degradation (brownout / bailout)">
            <SayCallout>“Health checks decide who gets traffic; degradation decides what work we do under stress.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Health checks (what each one means)</h3>
            <ul className="mb-3">
              <li>
                <strong>Liveness:</strong> “Is the process fundamentally broken?” Keep it <em>cheap</em> (event loop alive,
                threads not deadlocked, heap not exploding). If it fails → restart.
              </li>
              <li>
                <strong>Readiness:</strong> “Can this instance serve traffic right now?” Check <em>critical</em> deps only
                (config/secrets loaded, DB reachable, cache reachable). If it fails → remove from load balancer.
              </li>
              <li>
                <strong>Startup (K8s):</strong> delay readiness until warmup/migrations complete (avoid early 5xx).
              </li>
              <li>
                <strong>Pitfalls:</strong> deep dependency checks causing cascades; liveness killing pods during long GC.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Graceful degradation</h3>
            <ul className="mb-0">
              <li>
                <strong>Brownout:</strong> dim expensive features (turn off heavy ranking features, reduce fanout, fewer
                candidates).
              </li>
              <li>
                <strong>Bailout / Safe mode:</strong> serve minimal “core-only” response (cached feed, static shell, limited
                interactions).
              </li>
              <li>
                <strong>Mechanisms:</strong> feature flags, kill switches, cached fallbacks, smaller payloads, stale reads
                with a banner.
              </li>
            </ul>

            <TemplateCard title="Health + degradation — template (what to mention)">
{`• Liveness = restart; Readiness = remove from traffic; Startup = delay readiness during warmup.
• Degrade by dropping optional work first (brownout), then safe-mode if needed (bailout).
• Make degradation explicit: flags, fallbacks, cached/stale responses + user messaging.
• Ensure observability: how often we degrade + why + which feature toggles were active.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — health & degradation plan`}>
{`Readiness:
• Feed API is “ready” only if config + Redis + DB are reachable within a small probe budget.

Brownout triggers:
• If p95 > SLO or Redis CPU > 80% → disable expensive “people you may know” and reduce ranking feature set.
• If Kafka lag high → pause non-critical enrichment consumers.

Bailout:
• If ranking-service is down → serve cached feed head (stale 30–120s) + show “Some content may be delayed.”`}
            </ExampleCard>
          </Section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}

          {/* B) Retries, breakers, bulkheads */}
          <Section id="retries-breakers" title="B) Retries + jitter, circuit breakers, bulkheads (stop cascades)">
            <SayCallout>“Retries must be budgeted and jittered — otherwise they amplify outages.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Retries (safe rules)</h3>
            <ul className="mb-3">
              <li>
                Retry only <strong>idempotent</strong> calls (GET/PUT) or calls with an <strong>idempotency key</strong>.
              </li>
              <li>
                Use <strong>exponential backoff + full jitter</strong> to avoid synchronized retry storms.
              </li>
              <li>
                Add a <strong>retry budget</strong> per request (e.g., don’t burn a 2s SLO on 10 retries).
              </li>
              <li>
                Classify errors: retryable (timeouts/5xx/throttling) vs non-retryable (4xx validation).
              </li>
            </ul>

            <CodeCard title="Full jitter retry — template">
{`base=50ms; cap=500ms; attempts=3
for i in 1..attempts:
  sleep = rand(0, min(cap, base * 2^(i-1)))
  wait(sleep)
  try request
# Keep total wait bounded by your latency SLO.`}
            </CodeCard>

            <h3 className="h6 text-uppercase text-muted">Circuit breakers (fail fast)</h3>
            <ul className="mb-3">
              <li>
                <strong>Closed:</strong> normal traffic.
              </li>
              <li>
                <strong>Open:</strong> fail fast when error/timeout/latency crosses threshold.
              </li>
              <li>
                <strong>Half-open:</strong> allow small probes; close if healthy.
              </li>
              <li>Trip on both <strong>error rate</strong> and <strong>p95 latency</strong>.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Bulkheads (isolation)</h3>
            <ul className="mb-0">
              <li>
                Separate pools per dependency (DB pool vs Redis pool), per-tenant concurrency, separate queues for critical
                vs best-effort work.
              </li>
              <li>
                Prevent “one bad downstream” from consuming all threads/connections.
              </li>
            </ul>

            <TemplateCard title="Retries + breakers + bulkheads — template">
{`• Retries: only idempotent or idempotency-keyed operations.
• Backoff: exponential + full jitter; strict retry budget tied to SLO.
• Breakers: open on error+latency; half-open probes; fast recovery.
• Bulkheads: isolate pools per dependency and per tenant; cap in-flight and queue size.
• Goal: stop cascades and preserve the core endpoint SLO.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — stop the cascade`}>
{`Scenario: ranking-service starts timing out.
• Breaker opens for ranking-service calls (fail fast).
• Feed API switches to fallback: cached ranking / last-known good model output.
• Bulkheads keep DB/Redis pools healthy so login + privacy checks still work.
• Retries are limited (≤ 250ms total) so we don’t melt the service.`}
            </ExampleCard>
          </Section>

          {/* C) Rate limiting */}
          <Section id="rate-limit" title="C) Rate limiting, throttling, quotas (protect the system)">
            <SayCallout>“Rate limits protect shared capacity; quotas protect fairness across tenants/users.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Token bucket (most common)</h3>
            <ul className="mb-3">
              <li>
                Burst up to <em>B</em>, refill at rate <em>r</em> tokens/sec.
              </li>
              <li>
                Deny with <code>429</code> and <code>Retry-After</code>; optionally return rate-limit headers.
              </li>
              <li>Place it at edge/gateway and also near fragile deps (defense-in-depth).</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Leaky bucket / shaping</h3>
            <ul className="mb-3">
              <li>Leaky bucket smooths traffic into fragile dependencies.</li>
              <li>Great for DB write shaping, email provider, search, third-party APIs.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Quotas and cost-based limiting</h3>
            <ul className="mb-0">
              <li>
                Per user / per API key / per tenant / per org; reset daily/monthly.
              </li>
              <li>
                <strong>Weighted tokens:</strong> read=1, write=3, fanout=10, “expensive ranking”=5.
              </li>
              <li>
                Pitfall: only IP-based limits (NAT/office traffic); prefer authenticated identity.
              </li>
            </ul>

            <TemplateCard title="Rate limit — template (what to say)">
{`• Token bucket at the edge for fairness + abuse protection.
• Leaky bucket toward fragile dependencies to smooth spikes.
• Quotas are identity-based (API key/tenant/user), not IP-only.
• Use weighted costs so expensive endpoints can’t starve the system.
• Return correct headers (Retry-After) + monitor throttles as a signal.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — protect core traffic`}>
{`• Edge token bucket per user_id for read feed requests (burst allowed for scrolling).
• Separate stricter bucket for POST/engagement (likes/comments) to avoid write storms.
• Leaky bucket into DB for “write-heavy” actions.
• Weighted costs: ranking refresh is expensive → higher token cost than cached feed read.`}
            </ExampleCard>
          </Section>

          {/* D) Backpressure */}
          <Section id="backpressure" title="D) Backpressure (sync services + streams)">
            <SayCallout>“Backpressure means bounded resources + explicit shedding — not infinite queues.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Sync services (request/response)</h3>
            <ul className="mb-3">
              <li>Bound everything: request queue, thread pool, DB pool, upstream timeouts.</li>
              <li>
                Use watermarks: when queue/in-flight exceeds threshold → return <code>429</code>/<code>503</code> early.
              </li>
              <li>Adaptive concurrency: tune max in-flight from latency (like “AIMD” behavior).</li>
              <li>Load shed: drop optional enrichments first; reduce payload size.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Streams (Kafka/Kinesis)</h3>
            <ul className="mb-0">
              <li>Limit in-flight per partition; pause/resume consumption; autoscale on lag.</li>
              <li>Retry with DLQ, not infinite redelivery loops.</li>
              <li>Beware “commit before side effects”: commit offsets only after durable success.</li>
            </ul>

            <TemplateCard title="Backpressure — template (how to explain)">
{`• Bounded queues/pools everywhere. No unbounded buffers.
• Use watermarks: when saturation rises, shed load early with 429/503 (+ Retry-After).
• Prefer graceful degradation: drop optional work first.
• For streams: cap in-flight per partition, autoscale on lag, DLQ poison messages, commit after side effects.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — handling traffic spikes`}>
{`Sync:
• If in-flight > threshold → serve cached feed head; skip “recommendations” enrichment.
• If DB pool saturation → 503 with Retry-After for non-critical endpoints.

Streams:
• If consumer lag > SLO → pause low-priority topics (analytics), keep “privacy updates” high priority.
• DLQ malformed events; alert + replay tool.`}
            </ExampleCard>
          </Section>

          {/* E) Disaster recovery */}
          <Section id="dr" title="E) Disaster recovery (RTO/RPO), backups, restore drills">
            <SayCallout>“DR isn’t a document — it’s tested restores + scripted failover.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Key terms</h3>
            <ul className="mb-3">
              <li>
                <strong>RTO:</strong> time to restore service (e.g., 15 minutes).
              </li>
              <li>
                <strong>RPO:</strong> max acceptable data loss window (e.g., 60 seconds).
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Backups & restore readiness</h3>
            <ul className="mb-3">
              <li>Encrypted, immutable storage (WORM) + cross-region copies.</li>
              <li>Regular integrity checks and “can we actually restore?” drills.</li>
              <li>Practice table-level restore and whole-region restore.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Standby patterns</h3>
            <ul className="mb-0">
              <li>
                <strong>Cold:</strong> cheapest, slowest recovery.
              </li>
              <li>
                <strong>Pilot light:</strong> minimal services running, scale up during DR.
              </li>
              <li>
                <strong>Warm:</strong> partially running, faster failover.
              </li>
              <li>
                <strong>Hot:</strong> active-active or hot standby; most expensive.
              </li>
            </ul>

            <TemplateCard title="DR — template (what interviewers want to hear)">
{`• Define RTO/RPO per component (DB vs cache vs streams vs object store).
• Backups: encrypted + immutable + cross-region + verified.
• Drill restores on a schedule (quarterly is a good interview answer).
• Script failover: who triggers, cutover steps, DNS/GSLB, promotion with fencing, rollback.
• Include secrets/config replication + runbooks + communications plan.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — DR stance`}>
{`Targets:
• Feed read path: RTO 15m (can serve cached/stale feed), RPO 5–60s for engagement events.
• Privacy settings: stricter RPO (≤ 60s) and safer failover (fail closed if uncertain).

Mechanics:
• DB snapshots + WAL/CDC replication cross-region.
• Quarterly “restore to a new cluster” drill + scripted cutover.
• Promote leaders with fencing tokens to avoid split-brain after failover.`}
            </ExampleCard>
          </Section>

          {/* F) Multi-AZ vs Multi-region */}
          <Section id="multiaz-region" title="F) Multi-AZ vs multi-region (topology choices)">
            <SayCallout>“Multi-AZ is the default. Multi-region is a business decision with complexity costs.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Multi-AZ (within one region)</h3>
            <ul className="mb-3">
              <li>Protects from AZ/rack failure; low latency; simpler operations.</li>
              <li>Does not protect from full regional outage or provider-wide issues.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Multi-region</h3>
            <ul className="mb-0">
              <li>Protects from regional outage + improves geo latency.</li>
              <li>
                <strong>Active-passive:</strong> simpler invariants (single writer); failover via DNS/GSLB.
              </li>
              <li>
                <strong>Active-active:</strong> multi-writer; needs conflict handling (CRDT/LWW), idempotency, global IDs.
              </li>
            </ul>

            <TemplateCard title="Multi-AZ vs multi-region — template">
{`• Start with multi-AZ for most systems (cheap reliability win).
• Add multi-region if business requires: regulatory, extreme uptime, global latency.
• For strict invariants: active-passive (single write region) is simplest.
• For eventually consistent domains: active-active can work with conflict resolution + idempotency.
• Always test failover and measure the real RTO/RPO.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — topology choices`}>
{`• Feed reads: active-active across regions (serve nearest) with cached/stale tolerance.
• Privacy + account controls: active-passive single write region; fail closed during partitions.
• Engagement counters: active-active with CRDT merges (likes/views).
• Run GSLB with low TTL + region affinity; practice region evacuation.`}
            </ExampleCard>
          </Section>

          {/* G) Metrics & SLOs */}
          <Section id="metrics-slos" title="G) Metrics, SLOs & guardrails (what to monitor + what to automate)">
            <SayCallout>“I monitor golden signals + protection signals, and I rollback automatically on SLO burn.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Golden signals</h3>
            <ul className="mb-3">
              <li>
                <strong>Latency:</strong> p50/p95/p99 (and tail latency).
              </li>
              <li>
                <strong>Traffic:</strong> RPS, QPS, bytes in/out.
              </li>
              <li>
                <strong>Errors:</strong> 4xx/5xx, timeouts, dependency errors.
              </li>
              <li>
                <strong>Saturation:</strong> CPU, memory, thread pools, connection pools, queue depth.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Protection signals (early warning)</h3>
            <ul className="mb-3">
              <li>Retry rate, breaker opens/flaps, throttles (429), shed rate (503).</li>
              <li>Queue depth/consumer lag, DLQ rate, replica lag, cache hit ratio drop.</li>
              <li>Availability of dependencies (DB/Redis/Kafka), slow query rate.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Guardrails & rollouts</h3>
            <ul className="mb-0">
              <li>Canary rollout (1% → 5% → 25% → 100%) with automatic rollback on SLO breach.</li>
              <li>Error budget burn alerts (fast burn vs slow burn).</li>
              <li>Feature flags for brownout/bailout with audit trails.</li>
            </ul>

            <TemplateCard title="Metrics & SLOs — template (simple way to say it)">
{`• Define SLOs per user journey: read feed, post content, update privacy.
• Monitor golden signals + protection signals.
• Alert on error-budget burn (fast + slow), breaker flaps, lag SLO, replica lag.
• Ship with canaries + auto rollback. Degradation flags are part of the playbook.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — SLOs & alarms`}>
{`SLOs:
• Read feed p95 ${feed.slos.readP95}; write engagement p95 ${feed.slos.writeP95}.
• 99% of events processed within 5s; DLQ < 0.1%/day.

Alerts:
• Hit ratio drop > 10%/5m, breaker open > X/min, Kafka lag > threshold, DB pool saturation > 80%.
• Error budget fast burn triggers auto rollback and enables brownout flags.`}
            </ExampleCard>
          </Section>

          {/* H) Sound bites */}
          <Section id="soundbites" title="H) Sound bites you can reuse">
            <ul className="mb-0">
              <li>“Readiness isolates bad instances; brownout trims optional work; bailout protects core SLO.”</li>
              <li>“Retries are budgeted and jittered; breakers and bulkheads prevent cascades.”</li>
              <li>“Token buckets at the edge for fairness; leaky buckets to shape traffic into fragile deps.”</li>
              <li>“Backpressure is explicit: bounded queues, early 429/503, autoscale on lag.”</li>
              <li>“DR is tested restores + scripted failover; RTO/RPO are contractual.”</li>
              <li>“Multi-AZ by default; multi-region when the business needs it.”</li>
            </ul>

            <TemplateCard title="One clean closing line (template)">
{`“I keep the core alive with isolation + backpressure, and I prove it with drills: canaries, failovers, and restore tests.”`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — 20-second reliability answer`}>
{`“We protect the feed with readiness checks and graceful degradation. Retries have full jitter and a strict budget,
circuit breakers fail fast, and bulkheads isolate dependencies. We rate-limit at the edge, apply backpressure with
bounded queues, autoscale on lag, and drill DR with restore + scripted failover (RTO/RPO).”`}
            </ExampleCard>
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none order-xl-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
